// Plain three.js render of a mesh-only /ogs bundle: the rig GLB's skinned meshes
// under one untextured material, with the bundle's clip GLBs on an AnimationMixer.
// No Spark, no ONNX — the "does this mesh + animation look right?" sanity path.
import * as THREE from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js'
import { pruneClipTracks } from './pruneClipTracks.js'

export class MeshClipPreview {
  constructor() {
    this.root = null
    this.mixer = null
    this.actions = {}
    this.current = null
    this.hostScene = null
    this.material = null
  }

  // fetchBytes(url) → ArrayBuffer is injected so AAM auth stays in assetManagerApi.
  async load({ rigUrl, clips }, fetchBytes) {
    const draco = new DRACOLoader().setDecoderPath('/draco/')
    const loader = new GLTFLoader().setDRACOLoader(draco)
    const parse = (buf) => new Promise((res, rej) => loader.parse(buf, '', res, rej))
    try {
      const rig = await parse(await fetchBytes(rigUrl))
      this.root = rig.scene
      this.material = new THREE.MeshStandardMaterial({ color: 0xb8bcc4, roughness: 0.85, metalness: 0 })
      const nodeNames = new Set()
      this.root.traverse((o) => {
        nodeNames.add(o.name)
        if (!o.isMesh) return
        o.material = this.material
        o.frustumCulled = false // a posed skeleton can leave the bind-time bounds
        o.castShadow = true
      })
      this.mixer = new THREE.AnimationMixer(this.root)
      for (const c of clips) {
        const glb = await parse(await fetchBytes(c.url))
        const clip = glb.animations?.[0]
        if (!clip) { console.warn(`[mesh-preview] clip '${c.name}' has no animation`); continue }
        clip.name = c.name
        clip.tracks = pruneClipTracks(clip.tracks, nodeNames)
        if (!clip.tracks.length) { console.warn(`[mesh-preview] clip '${c.name}': no tracks bind to this rig`); continue }
        this.actions[c.name] = this.mixer.clipAction(clip)
      }
    } finally { draco.dispose() }
  }

  mount(scene) {
    this.hostScene = scene
    scene.add(this.root)
  }

  clipNames() { return Object.keys(this.actions) }

  // Name of the clip currently playing, or null.
  get playing() { return this.current?.isRunning() ? this.current.getClip().name : null }

  play(name) {
    const action = this.actions[name]
    if (!action) throw new Error(`mesh-preview: no clip '${name}' (have: ${this.clipNames().join(', ') || 'none'})`)
    this.current?.stop()
    this.current = action.reset().play()
  }

  stop() {
    this.current?.stop()
    this.current = null
  }

  // Called every render frame; allocation-free.
  update(delta) {
    if (this.current) this.mixer.update(delta)
  }

  worldBox() {
    this.root.updateMatrixWorld(true)
    return new THREE.Box3().setFromObject(this.root)
  }

  dispose() {
    this.stop()
    this.mixer?.stopAllAction()
    if (this.hostScene && this.root) this.hostScene.remove(this.root)
    this.root?.traverse((o) => o.geometry?.dispose?.())
    this.material?.dispose()
    this.root = null
    this.mixer = null
    this.actions = {}
    this.hostScene = null
  }
}
